import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, UnstyledButton, Group, Text, Center, Pagination, Flex, ScrollArea, Button, useMantineTheme, Skeleton, Modal, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { notifications } from '@mantine/notifications';
import { getBenefitTypes, deleteBenefitType } from '../../../services/api.service';
import BenefitTypeForm from '../../forms/BenefitTypeForm';
import { modals } from '@mantine/modals';
import DeleteButton from '../../buttons/DeleteButton';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const TypesTab = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [types, setTypes] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [typeEdit, setTypeEdit] = useState({});
	const [sortedData, setSortedData] = useState([]);
	const [sortBy, setSortBy] = useState(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getTypesList();
		// eslint-disable-next-line
	}, []);

	// Get benefit types
	const getTypesList = () => {

		// Get
		getBenefitTypes(function (response) {

			// Set benefit types and initial sorted data, remove loader
			setTypes(response.data);
			setSortedData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, reversed, sorted, onSort }) => {
		return (
			<th className={classes.th}>
				<UnstyledButton onClick={onSort} className={classes.control}>
					<Group position="apart">
						<Text fw={700} fz="sm">
							{children}
						</Text>
						<Center className={classes.icon}>
							{sorted ?
								(reversed ?
									<IconChevronUp size="0.9rem" stroke={1.5} />
									:
									<IconChevronDown size="0.9rem" stroke={1.5} />
								)
								: <IconSelector size="0.9rem" stroke={1.5} />}
						</Center>
					</Group>
				</UnstyledButton>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th
				sorted={sortBy === 'id'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('id')}
			>
				ID
			</Th>
			<Th
				sorted={sortBy === 'name'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('name')}
			>
				Nombre
			</Th>
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
					color="black"
				></Text>
			</th>
		</tr>
	);

	// Build rows
	const rows = sortedData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => toggleTypeEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.name}
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		setSortedData(sortData(types, { sortBy, reversed: reverseSortDirection, search: value }));
	};

	// Get current sort
	const setSorting = (field) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		setSortedData(sortData(types, { sortBy: field, reversed, search }));
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		return data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query)));
	}

	// Sort data
	const sortData = (data, payload) => {

		// Assign
		const { sortBy } = payload;

		// No filter
		if (!sortBy) { return filterData(data, payload.search); }

		// Return filtered data
		return filterData(
			[...data].sort((a, b) => {
				if (payload.reversed) {
					return b[sortBy].toString().localeCompare(a[sortBy]);
				}

				return a[sortBy].toString().localeCompare(b[sortBy]);
			}),
			payload.search
		);

	}

	// Close benefit types modal
	const closeModal = () => {
		// Get benefit types and close modal
		getTypesList();
		setIsEdit(false);
		setTypeEdit({});
		close();
	}

	// Toggle benefit type edit
	const toggleTypeEdit = (event, type) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setTypeEdit(type);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Tipo de Beneficio',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar este tipo de beneficio? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar tipo de beneficio', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeleteBenefitType(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete benefit type
	const triggerDeleteBenefitType = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteBenefitType(id, function () {

			// Show success
			notifications.show({
				title: 'Tipo de beneficio eliminado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Benefit Type Form */}
				<BenefitTypeForm
					closeModal={closeModal}
					isEdit={isEdit}
					benefitType={typeEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Flex direction="column">
				{/* Top */}
				<Flex mb="md">
					{/* New Benefit Type */}
					<Button
						onClick={open}
						variant="filled"
					>
						Nuevo Tipo de Beneficio
					</Button>
					{/* Search */}
					<TextInput
						placeholder="Buscar tipos de beneficio"
						icon={<IconSearch size="0.9rem" stroke={1.5} />}
						value={search}
						onChange={handleSearchChange}
						ml="sm"
						sx={{
							width: '100%',
							maxWidth: 350
						}}
						variant="filled"
					/>
				</Flex>
				{/* Table */}
				<ScrollArea>
					{isLoading ?
						<>
							<Skeleton height={40} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
						</>
						:
						<Table
							highlightOnHover
							sx={{ flex: 2 }}
						>
							<thead>{ths}</thead>
							<tbody>{rows.length > 0 ? (
								rows
							) : (
								<tr>
									<td colSpan={7}>
										<Text
											weight={500}
											align="center"
											my="xl"
											color="dimmed"
										>
											Sin resultados
										</Text>
									</td>
								</tr>
							)}</tbody>
						</Table>
					}
				</ScrollArea>
			</Flex>
			{/* Pages */}
			<Center sx={{ marginTop: 20 }}>
				<Pagination
					total={1}
					siblings={1}
					defaultValue={1}
					withEdges
				/>
			</Center>
		</>
	);

}

export default TypesTab;
